import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  AfterViewInit,
} from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { IMeetingEntity } from '../../../shared/core/state/live-class/models/live-class.dto';
@Component({
  selector: 'black-schedule-form',
  standalone: false,
  templateUrl: './schedule-form.component.html',
  styleUrls: ['./schedule-form.component.scss'],
})
export class ScheduleFormComponent implements AfterViewInit {
  private fb = inject(NonNullableFormBuilder);

  @Input() meetingSchedules: any[] = [];
  @Input() isLocked = false;

  @Output() addSchedule = new EventEmitter<(close: any) => void>();
  @Output() removeSchedule = new EventEmitter<number>();

  readonly durationOptions = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '1 hr' },
    { value: 90, label: '1 hr 30 min' },
    { value: 120, label: '2 hr' },
    { value: 150, label: '2 hr 30 min' },
    { value: 180, label: '3 hr' },
  ];

  schedulerForm = this.fb.group({
    title: [''],
    repeat: ['WEEKLY'],
    every: [1],
    onceDate: [''],
    from: ['00:00'],
    duration: [60],
    onTheMonthOption: ['option-1'],
    OnTheMonthDay: ['1'],
    onTheMonthWeek: [{ value: 'FIRST', disabled: true }],
    OnTheMonthWeekDay: [{ value: 'Monday', disabled: true }],
    onTheWeek: this.fb.group({
      monday: [false],
      tuesday: [false],
      wednesday: [false],
      thursday: [false],
      friday: [false],
      saturday: [false],
      sunday: [false],
    }),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    directJoinUrl: [''],
    feeOption: ['DEFAULT'], // 'DEFAULT' | 'FREE' | 'CUSTOM'
    customPrice: [0],
  });

  ngAfterViewInit(): void {
    // Render Scheduler Form Accordingly
    this.schedulerForm.controls.onTheMonthOption.valueChanges.subscribe(
      (val) => {
        if (val === 'option-1') {
          this.schedulerForm.controls.OnTheMonthWeekDay.disable();
          this.schedulerForm.controls.onTheMonthWeek.disable();
          this.schedulerForm.controls.OnTheMonthDay.enable();
        } else {
          this.schedulerForm.controls.OnTheMonthWeekDay.enable();
          this.schedulerForm.controls.onTheMonthWeek.enable();
          this.schedulerForm.controls.OnTheMonthDay.disable();
        }
      },
    );
  }

  get computedEndTime(): string {
    const from = this.schedulerForm.controls.from.value || '00:00';
    const duration = this.schedulerForm.controls.duration.value || 60;
    return this.calculateEndTime(from, duration);
  }

  private calculateEndTime(from: string, durationMinutes: number): string {
    const [h, m] = from.split(':').map(Number);
    const total = h * 60 + m + durationMinutes;
    const endH = Math.floor(total / 60) % 24;
    const endM = total % 60;
    return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  }

  addToMeetingsSchedule(callback: () => void) {
    if (this.isLocked) return;
    const raw = this.schedulerForm.getRawValue();
    const title = raw.title?.trim();
    if (!title) {
      this.schedulerForm.controls.title.setErrors({ required: true });
      this.schedulerForm.controls.title.markAsTouched();
      return;
    }

    const from = raw.from || '00:00';
    const duration = raw.duration || 60;
    const to = this.calculateEndTime(from, duration);

    let price: number | undefined = undefined;
    if (raw.feeOption === 'FREE') {
      price = 0;
    } else if (raw.feeOption === 'CUSTOM') {
      price = Number(raw.customPrice) || 0;
    }

    const isMonthly = raw.repeat === 'MONTHLY';
    const isWeekly = raw.repeat === 'WEEKLY';
    const isMonthlyDay =
      isMonthly &&
      (!raw.onTheMonthOption || raw.onTheMonthOption === 'option-1');
    const isMonthlyWeek = isMonthly && raw.onTheMonthOption === 'option-2';

    const meetingSchedule: IMeetingEntity = {
      title,
      repeat: raw.repeat,
      every: raw.every || 1,
      onceDate: raw.repeat === 'ONCE' ? raw.onceDate : undefined,
      from,
      to,
      onTheMonthOption: isMonthly ? raw.onTheMonthOption : undefined,
      OnTheMonthDay: isMonthlyDay && raw.OnTheMonthDay ? Number(raw.OnTheMonthDay) : undefined,
      onTheMonthWeek: isMonthlyWeek ? raw.onTheMonthWeek : undefined,
      OnTheMonthWeekDay: isMonthlyWeek ? raw.OnTheMonthWeekDay : undefined,
      onTheWeek: isWeekly ? raw.onTheWeek : undefined,
      timezone:
        raw.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      directJoinUrl: raw.directJoinUrl,
      price,
    };

    this.meetingSchedules.push(meetingSchedule);
    this.addSchedule.emit();
    this.resetSchedulerForm();
    callback();
  }

  resetSchedulerForm() {
    this.schedulerForm.reset({
      title: '',
      repeat: 'WEEKLY',
      every: 1,
      onceDate: '',
      from: '00:00',
      duration: 60,
      onTheMonthOption: 'option-1',
      OnTheMonthDay: '1',
      onTheMonthWeek: 'FIRST',
      OnTheMonthWeekDay: 'Monday',
      onTheWeek: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      directJoinUrl: '',
      feeOption: 'DEFAULT',
      customPrice: 0,
    });
    this.schedulerForm.controls.OnTheMonthWeekDay.disable();
    this.schedulerForm.controls.onTheMonthWeek.disable();
    this.schedulerForm.controls.OnTheMonthDay.enable();
  }

  removeMeetingsSchedule(index: number) {
    if (this.isLocked) return;
    if (index < this.meetingSchedules.length) {
      this.meetingSchedules.splice(index, 1);
      this.removeSchedule.emit(index);
    }
  }

  get schedulerEveryText() {
    if (this.schedulerForm.controls.repeat.value === 'ONCE') {
      return '';
    }
    if (this.schedulerForm.controls.repeat.value === 'DAILY') {
      return 'Day(s)';
    }
    if (this.schedulerForm.controls.repeat.value === 'WEEKLY') {
      return 'Week(s)';
    }
    if (this.schedulerForm.controls.repeat.value === 'MONTHLY') {
      return 'Month(s)';
    }
    return 'Day(s)';
  }

  fetchScheduleDescription(meetingSchedule: IMeetingEntity) {
    if (meetingSchedule.repeat === 'ONCE') {
      const dateStr = meetingSchedule.onceDate
        ? new Date(meetingSchedule.onceDate).toLocaleDateString()
        : 'selected date';
      return `This event will occur once on ${dateStr} at ${meetingSchedule.from} to ${meetingSchedule.to}`;
    }

    if (meetingSchedule.repeat === 'DAILY') {
      return `This event will occur every ${
        meetingSchedule.every > 1 ? meetingSchedule.every + ' days ' : 'day '
      }at ${meetingSchedule.from} to ${meetingSchedule.to}`;
    }

    if (meetingSchedule.repeat === 'WEEKLY') {
      const weekDays: string[] = [];
      for (const [key, value] of Object.entries(
        meetingSchedule.onTheWeek || {},
      )) {
        if (value) {
          weekDays.push(key[0].toUpperCase() + key.slice(1));
        }
      }
      return `This event will occur every ${
        meetingSchedule.every > 1 ? meetingSchedule.every + ' weeks ' : 'week '
      }on ${weekDays.join(',')} at ${meetingSchedule.from} to ${
        meetingSchedule.to
      }`;
    }

    if (meetingSchedule.repeat === 'MONTHLY') {
      const nthNumber = (number: number) => {
        return number > 0
          ? ['th', 'st', 'nd', 'rd'][
              (number > 3 && number < 21) || number % 10 > 3 ? 0 : number % 10
            ]
          : '';
      };

      let onMonthText = '';

      if (meetingSchedule.onTheMonthWeek && meetingSchedule.OnTheMonthWeekDay) {
        onMonthText = `on the ${meetingSchedule.onTheMonthWeek.toLowerCase()} ${
          meetingSchedule.OnTheMonthWeekDay
        }`;
      }

      if (meetingSchedule.OnTheMonthDay) {
        onMonthText = `on the ${meetingSchedule.OnTheMonthDay}${nthNumber(
          meetingSchedule.OnTheMonthDay,
        )}`;
      }

      return `This event will occur every ${
        meetingSchedule.every > 1 ? meetingSchedule.every + ' months' : 'month'
      } ${onMonthText} at ${meetingSchedule.from} to ${meetingSchedule.to}`;
    }

    return '';
  }

  numberArrays(start: number, end: number): number[] {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }
}
